/**
 * CLI handlers for `nexus client storage` commands.
 *
 * Upload, download, list, delete, and stats for storage files.
 * Connects to the running backend via nexus-client.
 */

import { readFileSync, writeFileSync, existsSync } from 'node:fs'
import { basename, resolve } from 'node:path'
import { consola } from 'consola'
import { getProjectPath } from '@kernel/config/paths.js'
import { createClient, getSession, cleanup, isConnectionError, handleConnectionError } from './client-commands.js'
import type { ClientOptions } from './client-commands.js'
import { mimeFromFilename } from '../modules/storage/mime.utils.js'

// ── Helpers ────────────────────────────────────────────────────────────────

function requireSession(): string {
  const projectPath = getProjectPath()
  const session = getSession(projectPath)
  if (!session) {
    consola.error('Not logged in. Run `nexus client login` first.')
    process.exit(1)
  }
  return projectPath
}

// ── nexus client storage upload ────────────────────────────────────────────

export async function handleStorageUpload(
  files: string[],
  options: ClientOptions & { folder?: string; scope?: string; visibility?: string }
): Promise<void> {
  requireSession()
  const { nexus } = await createClient(options)

  try {
    for (const filePath of files) {
      const absPath = resolve(filePath)
      if (!existsSync(absPath)) {
        consola.error(`File not found: ${absPath}`)
        cleanup(nexus, 1)
      }

      const buffer = readFileSync(absPath)
      const filename = basename(absPath)
      const mimetype = mimeFromFilename(filename)
      const file = new File([buffer], filename, { type: mimetype })

      const result = await nexus.storage.upload(file, {
        folder: options.folder,
        scope: options.scope,
        visibility: options.visibility as 'public' | 'internal' | 'private' | undefined,
      })

      console.log(JSON.stringify({
        id: result.id,
        filename: result.filename,
        mimetype: result.mimetype,
        size: result.size,
      }, null, 2))
    }
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    const apiError = err.response?.data?.error
    consola.error(apiError ? `Upload failed: ${apiError.message}` : `Upload failed: ${err.message}`)
    cleanup(nexus, 1)
  }
}

// ── nexus client storage download ──────────────────────────────────────────

export async function handleStorageDownload(
  id: string,
  options: ClientOptions & { output?: string }
): Promise<void> {
  const projectPath = requireSession()
  const { nexus } = await createClient(options)

  try {
    // Get file metadata for default filename
    const fileMeta = await nexus.storage.getFile(id)
    const outputPath = options.output
      ? resolve(options.output)
      : resolve(fileMeta.filename)

    // Download via fetch with auth token from session
    const downloadUrl = nexus.storage.getDownloadUrl(id)
    const session = getSession(projectPath)
    const headers: Record<string, string> = {}
    if (session?.accessToken) headers['Authorization'] = `Bearer ${session.accessToken}`

    const response = await fetch(downloadUrl, { headers })
    if (!response.ok) {
      consola.error(`Download failed: HTTP ${response.status}`)
      cleanup(nexus, 1)
    }

    const arrayBuffer = await response.arrayBuffer()
    writeFileSync(outputPath, Buffer.from(arrayBuffer))

    consola.success(`Downloaded: ${outputPath} (${fileMeta.size} bytes)`)
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    consola.error(`Download failed: ${err.message}`)
    cleanup(nexus, 1)
  }
}

// ── nexus client storage list ──────────────────────────────────────────────

export async function handleStorageList(
  options: ClientOptions & { mimetype?: string; folder?: string; scope?: string; limit?: string }
): Promise<void> {
  requireSession()
  const { nexus } = await createClient(options)

  try {
    const params: Record<string, unknown> = {}
    if (options.limit) params['limit'] = Number(options.limit)
    if (options.mimetype) params['filter_mimetype'] = options.mimetype
    if (options.folder) params['filter_folder'] = options.folder
    if (options.scope) params['filter_scope'] = options.scope

    const result = await nexus.http.get<{ items: unknown[]; total: number }>('/storage/files', { params })
    console.log(JSON.stringify(result, null, 2))
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    consola.error(`List failed: ${err.message}`)
    cleanup(nexus, 1)
  }
}

// ── nexus client storage delete ────────────────────────────────────────────

export async function handleStorageDelete(
  id: string,
  options: ClientOptions
): Promise<void> {
  requireSession()
  const { nexus } = await createClient(options)

  try {
    await nexus.storage.delete(id)
    consola.success(`Deleted: ${id}`)
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    const apiError = err.response?.data?.error
    consola.error(apiError ? `Delete failed: ${apiError.message}` : `Delete failed: ${err.message}`)
    cleanup(nexus, 1)
  }
}

// ── nexus client storage stats ─────────────────────────────────────────────

export async function handleStorageStats(
  options: ClientOptions
): Promise<void> {
  requireSession()
  const { nexus } = await createClient(options)

  try {
    const [stats, byType] = await Promise.all([
      nexus.http.get<Record<string, unknown>>('/storage/stats'),
      nexus.http.get<{ x: string; y: number }[]>('/storage/stats/by-type'),
    ])

    console.log(JSON.stringify({ ...stats as object, by_type: byType }, null, 2))
    cleanup(nexus)
  } catch (err: any) {
    if (isConnectionError(err)) handleConnectionError()
    consola.error(`Stats failed: ${err.message}`)
    cleanup(nexus, 1)
  }
}
